import React, {useState} from 'react';
import styled from 'styled-components/native';
import TextGeneric from '../textGeneric';
import {useTheme} from 'styled-components';
import {Theme} from '../../theme/lightTheme';
import {DrugItemType} from '../../interfaces/api/tabs';
import {RefreshControl, View, ScrollView} from 'react-native';
import useResponsiveDimensions from '../../utils/hooks/useResponsiveDimensions';
import EmptyList from '../emptyList';
import DrugFiltersListSkeleton from '../drugFiltersListSkeleton';
import Icon from '../icon';
import {tr} from '../../translations';
import Animated, {
  FadeInDown,
  FadeInUp,
  FadeOut,
  FadeOutUp,
} from 'react-native-reanimated';
import Loader from '../loader';
import GenericButton from '../button';
import {SelectDrugDiseaseProps} from '../../interfaces/components/selectDrugDisease';

const Container = styled.View`
  width: 100%;
`;
const ContentContainer = styled.View`
  width: 100%;
  background-color: ${({theme}) => theme.screenBackground};
`;
const TitleContainer = styled.View`
  width: 100%;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  background-color: ${({theme}) => theme.text.dark + '22'};
  overflow: hidden;
  padding: ${({theme}) => theme.text.s6}px;
`;
const Title = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s6}px;
  color: ${({theme}) => theme.text.dark};
`;
const ItemContainer = styled.TouchableOpacity<{isRight?: boolean}>`
  width: 50%;
  height: 100%;
  border-left-width: ${({isRight}) => (isRight ? 1 : 0)}px;
  border-left-color: ${({theme}) => theme.border + '77'};
`;
const ItemText = styled(TextGeneric)<{isSelected?: boolean}>`
  font-size: ${({theme}) => theme.text.s6}px;
  padding: ${({theme}) => theme.text.s7}px;
  color: ${({theme, isSelected}) =>
    isSelected ? theme.settings.icon : theme.text.dark};
`;
const Separator = styled.View`
  width: 100%;
  height: 1px;
  background-color: ${({theme}) => theme.border + '77'};
`;
const EmptyContainer = styled.View`
  align-items: center;
  justify-content: center;
  overflow: hidden;
  height: 100%;
`;
const EmptyText = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s7}px;
  color: ${({theme}) => theme.text.dark};
  width: 80%;
  padding-vertical: ${({theme}) => theme.text.s55}px;
  text-align: center;
`;
const FooterContainer = styled(Animated.View)`
  padding-vertical: ${({theme}) => theme.text.s55}px;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
`;
const RowContainer = styled.View`
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

/**
 * SelectDrugDisease component .
 *
 * @param {SelectDrugDiseaseProps} props - Component props
 * @returns {React.ReactElement} The SelectDrugDisease component
 */
const SelectDrugDisease: React.FC<SelectDrugDiseaseProps> = ({
  style,
  title,
  data,
  onEndReached,
  onRefresh,
  isLoading,
  isError,
  onDrugDiseaseSelected,
  height,
}: SelectDrugDiseaseProps): React.ReactElement => {
  const theme = useTheme() as Theme;
  const {hdp, getByScreenSize} = useResponsiveDimensions();
  const [selected, setSelected] = useState<any>(null);

  // Handle scroll to bottom for loading more
  const handleScroll = ({nativeEvent}: {nativeEvent: any}) => {
    const {layoutMeasurement, contentOffset, contentSize} = nativeEvent;
    const paddingToBottom = 20;
    if (
      layoutMeasurement.height + contentOffset.y >=
      contentSize.height - paddingToBottom
    ) {
      // Load more when scrolled to bottom
      onEndReached?.();
    }
  };

  const renderItem = (item: DrugItemType, index: number) => {
    const isSelected = selected?.id === item?.id;
    return (
      <ItemContainer
        isRight={index % 2 !== 0}
        key={item.id}
        onPress={() => setSelected(item)}>
        <ItemText bold={isSelected} isSelected={isSelected}>
          {item.name || ''}
        </ItemText>
      </ItemContainer>
    );
  };

  const renderRow = (rowItems: DrugItemType[], rowIndex: number) => (
    <View key={`row-${rowIndex}`}>
      <RowContainer>
        {rowItems.map((item, index) => renderItem(item, index))}
      </RowContainer>
      {rowIndex < Math.ceil((data || []).length / 2) - 1 && <Separator />}
    </View>
  );

  const renderGridItems = () => {
    const rows = [];
    const itemsData = data || [];

    // Create rows with 2 items each
    for (let i = 0; i < itemsData.length; i += 2) {
      const rowItems = itemsData.slice(i, i + 2);
      // If odd number of items, add empty placeholder to maintain layout
      if (rowItems.length === 1) {
        rowItems.push({} as DrugItemType);
      }
      rows.push(renderRow(rowItems, i / 2));
    }

    return rows;
  };

  const _renderEmpty = () => {
    if (isLoading) {
      return (
        <View
          style={{
            paddingTop: '2%',
          }}>
          <DrugFiltersListSkeleton />
        </View>
      );
    }
    if (isError) {
      return (
        <EmptyContainer>
          <EmptyList
            iconName={'alert'}
            iconColor={theme.primary}
            iconSize={theme.text.s1 * 2}
            onRefreshPress={onRefresh}
            title={'app.error'}
            showRefresh
          />
        </EmptyContainer>
      );
    }
    return (
      <EmptyContainer>
        <View>
          <Icon
            name={'webClock'}
            size={theme.text.s1 * 2}
            color={theme.primary}
          />
        </View>
        <EmptyText semiBold>{tr('drugsAndDiseases.empty')}</EmptyText>
      </EmptyContainer>
    );
  };

  const _renderFooter = () => {
    if (isLoading && (data || [])?.length > 0) {
      return (
        <FooterContainer entering={FadeInUp} exiting={FadeOutUp}>
          <Loader />
        </FooterContainer>
      );
    }
    return null;
  };

  const onDrugDiseaseSelectedPressed = () => {
    onDrugDiseaseSelected?.(selected);
    setSelected(null);
  };

  return (
    <Container style={style}>
      <TitleContainer>
        <Title semiBold>{title || ''}</Title>
      </TitleContainer>
      <ContentContainer>
        <View
          style={{
            backgroundColor: theme.screenBackground,
            flexGrow: 1,
            height: height || hdp(getByScreenSize(35, 25)),
          }}>
          <ScrollView
            nestedScrollEnabled
            contentContainerStyle={{
              backgroundColor: theme.screenBackground,
              flexGrow: 1,
            }}
            onScroll={handleScroll}
            scrollEventThrottle={16}
            refreshControl={
              <RefreshControl
                tintColor={theme.primary}
                refreshing={!!isLoading && data?.length === 0}
                onRefresh={onRefresh}
              />
            }>
            {(data || []).length > 0 ? renderGridItems() : _renderEmpty()}
            {_renderFooter()}
          </ScrollView>
        </View>
        {!!selected && (
          <Animated.View entering={FadeInDown} exiting={FadeOut}>
            <GenericButton
              radius={100}
              label={tr('drugsAndDiseases.confirmSelection')}
              onPress={onDrugDiseaseSelectedPressed}
              style={{
                width: getByScreenSize('90%', '40%'),
                alignSelf: 'center',
                marginVertical: theme.text.s10,
              }}
            />
          </Animated.View>
        )}
      </ContentContainer>
    </Container>
  );
};

export default SelectDrugDisease;
